import { useCallback, useEffect, useRef, useState } from 'react'
import {
  api, ApiError, can,
  type FeedbackDetail, type FeedbackItem, type FeedbackKind, type FeedbackStatus,
} from '../lib/api'
import {
  CUSTOMER_WORDS, KindChip, StatusPill, Steps, Thread, day, stamp,
} from './FeedbackParts'
import { useSplit } from '../lib/split'
import './feedback.css'

/** REPORT A BUG, REQUEST A FEATURE, AND FOLLOW IT (migration 065, 5 Oct 2026).
 *
 *  The operator: "the customer should be able to see all his bugs that he
 *  reported, all his features that he requested, and which one was accepted
 *  and which one was implemented already." It replaces a spreadsheet passed
 *  back and forth.
 *
 *  A USER SEES ONLY WHAT HE SENT; the organization's administrator sees all
 *  of it. That is decided on the server -- this screen draws what it is
 *  handed and asks nothing about whose a report is.
 *
 *  NOT FOR EMERGENCIES, and the form says so above everything else: nobody
 *  must report a live dispatch failure here and wait. */

const POLL_MS = 30_000

/** HOW THE LIST IS NARROWED. The operator, the day it went live with thirty-
 *  two items in it: "the customer should be able to sort it by his requests,
 *  what's done, what's in process, what's not accepted ... Now it's one big
 *  list, so it's even hard for the customer to see all his tickets."
 *
 *  SIX BUTTONS, EACH WITH ITS COUNT -- the shape of the four above Open
 *  calls. The tab OPENS ON `open`, everything still being worked on, so the
 *  finished ones are a press away rather than in the way (his Q2 A).
 *
 *  `grouped` IS HIS "ALL" BUTTON: everything, under headings that open and
 *  close, Done and Declined shut to begin with. A flat list of everything is
 *  the thing he was objecting to. */
type StatusView = 'open' | 'need_info' | 'in_progress' | 'accepted' | 'done' | 'declined'
type ListView = StatusView | 'grouped'
const VIEWS: { key: StatusView; label: string; fits: (s: FeedbackStatus) => boolean }[] = [
  { key: 'open', label: 'Open', fits: (s) => s !== 'done' && s !== 'declined' },
  { key: 'need_info', label: 'Waiting on you', fits: (s) => s === 'need_info' },
  { key: 'in_progress', label: 'In progress', fits: (s) => s === 'in_progress' },
  { key: 'accepted', label: 'Accepted', fits: (s) => s === 'accepted' },
  { key: 'done', label: 'Done', fits: (s) => s === 'done' },
  { key: 'declined', label: 'Declined', fits: (s) => s === 'declined' },
]
/** The grouped view's headings, the ones that need him first. A Record, so a
 *  status added to the product and forgotten here does not compile -- left
 *  out of a plain list it would be invisible under "All". */
const GROUP_ORDER: Record<FeedbackStatus, number> = {
  need_info: 0, in_progress: 1, accepted: 2, new: 3, done: 4, declined: 5,
}
const GROUPS = (Object.keys(GROUP_ORDER) as FeedbackStatus[])
  .sort((a, b) => GROUP_ORDER[a] - GROUP_ORDER[b])
const KINDS: { key: 'all' | FeedbackKind; label: string }[] = [
  { key: 'all', label: 'Bugs and features' },
  { key: 'bug', label: 'Bugs' },
  { key: 'feature', label: 'Features' },
]
/** The server's ceiling on one picture. Checked here too, because anything
 *  over the proxy's own limit is refused there with no sentence at all. */
const MAX_PICTURE_BYTES = 5 * 1024 * 1024

function Form({ cameFrom, onSent, onCancel }: {
  cameFrom: string
  onSent: (item: FeedbackItem, note: string | null) => void
  onCancel: () => void
}) {
  const [kind, setKind] = useState<FeedbackKind>('bug')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileNote, setFileNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send() {
    if (busy || !title.trim()) return
    setBusy(true)
    setError(null)
    try {
      const item = await api.feedbackCreate({
        kind, title: title.trim(), body: body.trim() || null, screen: cameFrom || null,
      })
      // THE REPORT IS SENT BEFORE THE PICTURE IS TRIED. A screenshot the
      // server refuses must not cost him what he typed.
      let note: string | null = null
      if (file) {
        try {
          await api.feedbackAttach(item.id, file)
        } catch (e) {
          note = `Your report was sent. The picture was not: ${
            e instanceof ApiError ? e.message : 'it could not be uploaded.'}`
        }
      }
      onSent(item, note)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send the report.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fb-bd">
      <div className="fb-caution">
        <b>Not for emergencies.</b> If dispatch is not working right now, call Advance
        Talk. This form is read during office hours.
      </div>
      <div className="fb-field">
        <label>What is it?</label>
        <div className="fb-seg">
          <button type="button" className={kind === 'bug' ? 'fb-on' : ''}
                  onClick={() => setKind('bug')}>Something is broken</button>
          <button type="button" className={kind === 'feature' ? 'fb-on' : ''}
                  onClick={() => setKind('feature')}>I would like a new feature</button>
        </div>
      </div>
      <div className="fb-field">
        <label>In one line</label>
        <input type="text" value={title} maxLength={200} disabled={busy}
               onChange={(e) => setTitle(e.target.value)} autoFocus />
      </div>
      <div className="fb-field">
        <label>{kind === 'bug' ? 'What happened, and what did you expect?' : 'What should it do?'}</label>
        <textarea rows={5} value={body} maxLength={8000} disabled={busy}
                  onChange={(e) => setBody(e.target.value)} />
      </div>
      <div className="fb-field">
        <label>Screenshot (optional)</label>
        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" disabled={busy}
               onChange={(e) => {
                 const picked = e.target.files?.[0] ?? null
                 if (picked && picked.size > MAX_PICTURE_BYTES) {
                   setFile(null)
                   setFileNote('That picture is larger than 5 MB, so it is left out. Pick a smaller one.')
                   e.target.value = ''
                   return
                 }
                 setFileNote(null)
                 setFile(picked)
               }} />
        <div className="fb-hint" style={{ marginTop: 4 }}>
          {fileNote ?? 'Seen by Advance Talk support. Leave it out if it shows a caller\'s details.'}
        </div>
      </div>
      {error && <div className="fb-note fb-bad">{error}</div>}
      <div className="fb-line" style={{ marginTop: 12 }}>
        <button type="button" className="fb-btn fb-primary" disabled={busy || !title.trim()}
                onClick={send}>
          {busy ? 'Sending…' : 'Send to Advance Talk'}
        </button>
        <button type="button" className="fb-btn" disabled={busy} onClick={onCancel}>Cancel</button>
        <span className="fb-hint">
          Your name{cameFrom ? `, the screen you came from (${cameFrom})` : ''} and the time go with it.
        </span>
      </div>
    </div>
  )
}

export function Feedback({ cameFrom, onRead }: {
  /** The screen he was on when he pressed Feedback, in its own words. */
  cameFrom: string
  /** The count on the tab should be looked at again. */
  onRead?: () => void
}) {
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [viewAll, setViewAll] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [detail, setDetail] = useState<FeedbackDetail | null>(null)
  const [composing, setComposing] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)
  const onReadRef = useRef(onRead)
  useEffect(() => { onReadRef.current = onRead }, [onRead])
  /** WHICH REPORT IS OPEN, readable by a response that arrives late. A read
   *  of A landing after B was opened used to put A's thread on screen while
   *  the reply box still posted to B -- an answer filed, for good, on the
   *  wrong report. A response for anything but the open report is dropped. */
  const openRef = useRef<string | null>(null)
  const mayReport = can('feedback.report')
  const [view, setView] = useState<ListView>('open')
  /** The status he was on before pressing All, to go back to. */
  const [before, setBefore] = useState<StatusView>('open')
  const [kind, setKind] = useState<'all' | FeedbackKind>('all')
  const [query, setQuery] = useState('')
  const [byNumber, setByNumber] = useState(false)
  /** Which headings of the grouped view are shut. The finished ones, to
   *  begin with. */
  const [shut, setShut] = useState<Partial<Record<FeedbackStatus, boolean>>>(
    { done: true, declined: true })
  /** The divider between the list and the report can be pulled; how far is
   *  remembered in this browser. */
  const split = useSplit('feedback.split')

  const loadList = useCallback(async () => {
    try {
      const res = await api.feedbackList()
      setItems(res.items)
      setViewAll(res.view_all)
      setListError(null)
    } catch (e) {
      setListError(e instanceof ApiError ? e.message : 'Could not load the reports.')
    }
  }, [])
  useEffect(() => {
    loadList()
    const t = setInterval(loadList, POLL_MS)
    return () => clearInterval(t)
  }, [loadList])

  /** OPENING A REPORT READS IT, for the person it belongs to. The server
   *  decides whose mark it is; this only asks for the count again. */
  const loadDetail = useCallback(async (id: string) => {
    try {
      const d = await api.feedbackRead(id)
      if (openRef.current !== id) return
      setDetail(d)
      if (d.unread) {
        setItems((l) => l.map((i) => (i.id === id ? { ...i, unread: false } : i)))
        onReadRef.current?.()
      }
    } catch (e) {
      if (openRef.current !== id) return
      if (e instanceof ApiError && e.status === 404) { setOpenId(null); setDetail(null) }
    }
  }, [])
  useEffect(() => {
    openRef.current = openId
    setText('')
    setDetail(null)
    if (!openId) return
    loadDetail(openId)
    const t = setInterval(() => loadDetail(openId), POLL_MS)
    return () => clearInterval(t)
  }, [openId, loadDetail])

  async function reply() {
    // TO THE REPORT ON SCREEN, by its own id -- never to a remembered one.
    if (!detail || busy || !text.trim()) return
    const id = detail.id
    setBusy(true)
    setNote(null)
    try {
      const d = await api.feedbackReply(id, text.trim())
      if (openRef.current === id) { setDetail(d); setText('') }
      loadList()
    } catch (e) {
      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Could not send the reply.' })
    } finally {
      setBusy(false)
    }
  }

  const showing = composing || !!openId

  // NARROWED IN THE BROWSER, from the list the server already decided this
  // login may see: nothing here widens it. The counts on the buttons are
  // counts of what the kind buttons and the search box leave.
  const term = query.trim().toLowerCase()
  const wanted = term.replace(/^#/, '')
  // A SEARCH LOOKS AT EVERYTHING: every status and both kinds. A number
  // typed while Done is chosen, or while only Features are shown, must still
  // find a bug that is only Accepted -- "no report matches" for a report
  // that exists reads as a report that was lost. So while a term is typed
  // the buttons are all unlit, and pressing one of them leaves the search.
  const base = term
    ? items.filter((i) =>
        i.title.toLowerCase().includes(term)
        || i.body.toLowerCase().includes(term)
        || String(i.number) === wanted)
    : items.filter((i) => kind === 'all' || i.kind === kind)
  const fits = term || view === 'grouped'
    ? () => true
    : (VIEWS.find((v) => v.key === view) ?? VIEWS[0]).fits
  const rows = base.filter((i) => fits(i.status))
  const ordered = byNumber ? [...rows].sort((a, b) => a.number - b.number) : rows

  const row = (i: FeedbackItem) => (
    <button type="button" key={i.id}
            className={`fb-row fb-s-${i.status} ${i.id === openId && !composing ? 'fb-on' : ''}`}
            onClick={() => { setComposing(false); setOpenId(i.id); setNote(null) }}>
      <span className="fb-num">#{i.number}</span>
      <span className="fb-t">
        {i.unread && <span className="fb-dot" title="Something new to read" />}
        <KindChip kind={i.kind} />{i.title}
      </span>
      <StatusPill status={i.status} at={i.status_at} forCustomer />
      <span className="fb-m">
        sent {day(i.created_at)}
        {viewAll && i.reported_by_label ? ` · by ${i.reported_by_label}` : ''}
        {i.entered_by_operator ? ' · entered by Advance Talk' : ''}
        {day(i.last_activity_at) !== day(i.created_at) ? ` · last activity ${day(i.last_activity_at)}` : ''}
      </span>
    </button>
  )

  return (
    <div ref={split.box}
         className={`fb fb-split ${showing ? 'fb-showing' : ''} ${split.dragging ? 'fb-dragging' : ''}`}
         style={split.width === null ? undefined : { ['--fb-list' as string]: `${split.width}px` }}>
      <div className="fb-col fb-list">
        <h2>
          {viewAll ? 'Reports from this organization' : 'My reports'}
          <span className="fb-spacer" />
          {mayReport && (
            <button type="button" className="fb-btn fb-primary fb-sm"
                    onClick={() => { setComposing(true); setOpenId(null); setNote(null) }}>
              + Report a bug or request a feature
            </button>
          )}
        </h2>
        {items.length > 0 && (
          <>
            <div className="fb-views" role="group" aria-label="Which reports">
              {VIEWS.map((v) => {
                const inside = base.filter((i) => v.fits(i.status))
                const n = inside.length
                // THE DOT IS WHY HE CAME. A report moved to Done lights the
                // count on the tab -- and Done is not what this list opens
                // on. Without a mark on the button that holds it, the tab
                // said "1" and the screen showed nothing new anywhere.
                const news = inside.some((i) => i.unread)
                const chosen = view === v.key && !term
                return (
                  <button type="button" key={v.key} aria-pressed={chosen}
                          aria-label={`${v.label}: ${n}${news ? ', something new' : ''}`}
                          className={`fb-view fb-v-${v.key} ${chosen ? 'fb-on' : ''} ${n ? '' : 'fb-zero'}`}
                          onClick={() => { setView(v.key); setQuery('') }}>
                    <span>{news && <span className="fb-dot" />}{v.label}</span><b>{n}</b>
                  </button>
                )
              })}
            </div>
            <div className="fb-tools">
              {KINDS.map((k) => {
                const chosen = kind === k.key && !term
                return (
                  <button type="button" key={k.key} aria-pressed={chosen}
                          className={`fb-chip ${chosen ? 'fb-on' : ''}`}
                          onClick={() => { setKind(k.key); setQuery('') }}>
                    {k.label}
                  </button>
                )
              })}
              <span className="fb-sep" />
              <button type="button" title="Everything, grouped by status"
                      aria-pressed={view === 'grouped'}
                      className={`fb-chip ${view === 'grouped' ? 'fb-on' : ''}`}
                      onClick={() => {
                        // A second press goes back to where he was.
                        if (view === 'grouped') { setView(before); return }
                        setBefore(view)
                        setView('grouped')
                      }}>
                All
              </button>
              {view !== 'grouped' && (
                <select className="fb-select" value={byNumber ? 'number' : 'recent'}
                        aria-label="Order" onChange={(e) => setByNumber(e.target.value === 'number')}>
                  <option value="recent">Latest activity first</option>
                  <option value="number">By number</option>
                </select>
              )}
              <input className="fb-search" type="search" value={query}
                     aria-label="Search the reports by words or by number"
                     placeholder="Search by words or by number"
                     onChange={(e) => setQuery(e.target.value)} />
            </div>
            {term && (
              <div className="fb-searching">
                Looking at everything, whatever its status or kind · {base.length} found
              </div>
            )}
          </>
        )}
        <div className="fb-scroll">
          {listError && <div className="fb-bd"><div className="fb-note fb-bad">{listError}</div></div>}
          {!listError && items.length === 0 && (
            <div className="fb-empty">
              <div className="fb-big">Nothing reported yet</div>
              <p>What is sent to Advance Talk appears here, with what became of it.</p>
            </div>
          )}
          {items.length > 0 && view === 'grouped' && GROUPS.map((g) => {
            const inGroup = base.filter((i) => i.status === g).sort((a, b) => a.number - b.number)
            if (inGroup.length === 0) return null
            // HELD OPEN by a search (what it found must not be folded away)
            // and by anything unread inside (it is why he came). While it is
            // held, the heading does not answer a press: one that did
            // nothing on screen and then left the heading shut once the
            // search was cleared would hide live reports behind his back.
            const news = inGroup.some((i) => i.unread)
            const held = !!term || news
            const closed = !!shut[g] && !held
            return (
              <div className="fb-group" key={g}>
                <button type="button" className="fb-grouphead" aria-expanded={!closed}
                        aria-disabled={held}
                        onClick={() => { if (!held) setShut((s) => ({ ...s, [g]: !s[g] })) }}>
                  <span className="fb-arrow">{closed ? '▸' : '▾'}</span>
                  <span className="fb-g">{news && <span className="fb-dot" />}{CUSTOMER_WORDS[g]}</span>
                  <span className="fb-c">{inGroup.length}</span>
                </button>
                {!closed && inGroup.map(row)}
              </div>
            )
          })}
          {items.length > 0 && view !== 'grouped' && ordered.map(row)}
          {items.length > 0 && (view === 'grouped' ? base.length === 0 : ordered.length === 0) && (
            <div className="fb-empty">
              {term ? (
                <div className="fb-big">No report matches that</div>
              ) : base.length === 0 ? (
                // The kind button is the cause, so it is the way out: "Show
                // all" from here led to a second empty screen.
                <>
                  <div className="fb-big">No {kind === 'bug' ? 'bugs' : 'features'} reported yet</div>
                  <button type="button" className="fb-btn fb-sm" onClick={() => setKind('all')}>
                    Show bugs and features
                  </button>
                </>
              ) : (
                <>
                  <div className="fb-big">Nothing here</div>
                  <button type="button" className="fb-btn fb-sm"
                          onClick={() => { setBefore(view === 'grouped' ? 'open' : view); setView('grouped') }}>
                    Show all
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="fb-splitter" {...split.handle}><span /></div>

      <div className="fb-col fb-detail">
        {composing ? (
          <>
            <h2>
              <button type="button" className="fb-btn fb-sm fb-back" onClick={() => setComposing(false)}>← Back</button>
              A new report
            </h2>
            <div className="fb-scroll">
              <Form
                cameFrom={cameFrom}
                onCancel={() => setComposing(false)}
                onSent={(item, problem) => {
                  setComposing(false)
                  // BACK TO WHERE A NEW REPORT IS: Open, both kinds, no
                  // search. Sent while Done was chosen, it was on the right
                  // as "Sent as #33" and nowhere in the list on the left.
                  setView('open')
                  setKind('all')
                  setQuery('')
                  setOpenId(item.id)
                  setNote(problem
                    ? { ok: false, text: problem }
                    : { ok: true, text: `Sent as #${item.number}. Advance Talk has been told.` })
                  loadList()
                }}
              />
            </div>
          </>
        ) : !detail ? (
          <div className="fb-empty">
            <div className="fb-big">{openId ? 'Loading…' : 'Pick a report'}</div>
            {!openId && mayReport && <p>Or press the button to send a new one.</p>}
          </div>
        ) : (
          <>
            <h2>
              <button type="button" className="fb-btn fb-sm fb-back" onClick={() => setOpenId(null)}>← Back</button>
              #{detail.number}
              <span className="fb-spacer" />
              <StatusPill status={detail.status} at={detail.status_at} forCustomer />
            </h2>
            <div className="fb-scroll">
              <div className="fb-bd">
                <div style={{ marginBottom: 10 }}>
                  <KindChip kind={detail.kind} /><b>{detail.title}</b>
                </div>
                <Steps status={detail.status} createdAt={detail.created_at} events={detail.events} />
                <div className="fb-thread">
                  <div className="fb-say fb-them">
                    {detail.body || '(no description)'}
                    {detail.attachments.length > 0 && (
                      <div className="fb-shots">
                        {detail.attachments.map((a) => (
                          <img key={a.id} src={api.feedbackPictureUrl(a.id)} alt="Screenshot sent with the report"
                               onClick={() => window.open(api.feedbackPictureUrl(a.id), '_blank', 'noopener')} />
                        ))}
                      </div>
                    )}
                    <span className="fb-meta">
                      {detail.reported_by_label ?? 'This organization'} · {stamp(detail.created_at)}
                      {detail.screen ? ` · sent from: ${detail.screen}` : ''}
                      {detail.entered_by_operator ? ' · entered by Advance Talk' : ''}
                    </span>
                  </div>
                </div>
                <div style={{ height: 8 }} />
                <Thread events={detail.events} />
              </div>
            </div>
            {(mayReport || note) && (
              <div className="fb-foot">
                {note && <div className={`fb-note ${note.ok ? '' : 'fb-bad'}`}>{note.text}</div>}
                {mayReport && (
                  <div className="fb-reply">
                    <textarea rows={2} value={text} disabled={busy} placeholder="Reply to Advance Talk…"
                              onChange={(e) => setText(e.target.value)} />
                    <button type="button" className="fb-btn fb-primary" disabled={busy || !text.trim()}
                            onClick={reply}>
                      {busy ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
